import type { ReactNode } from 'react'
import type { ReferralAttribution } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { Button } from '@/shared/ui/button.tsx'
import {
  CardActions,
  CardFact,
  CardFacts,
  CardHeading,
  DesktopTable,
  MobileCard,
  MobileCardList,
} from '@/shared/ui/responsive-table.tsx'
import { ACCOUNT_KIND_LABELS, STATUS_LABELS, STATUS_TONES, sourceLabel, suspiciousReasonLabel } from '../labels.ts'

const HEADERS = ['Приглашённый', 'Тип', 'Статус', 'Как привязан', 'Подозрение']

interface AttributionsTableProps {
  attributions: ReferralAttribution[]
  onRevert: (attribution: ReferralAttribution) => void
}

interface AttributionProps {
  attribution: ReferralAttribution
}

const isFlagged = (attribution: ReferralAttribution): boolean =>
  attribution.suspicious && attribution.status !== 'reverted'

function Sub({ children }: { children: ReactNode }) {
  return <div className="mt-0.5 text-[12px] text-ink-muted">{children}</div>
}

function InviteeCell({ invitee, titleClassName }: { invitee: ReferralAttribution['invitee']; titleClassName?: string }) {
  return (
    <>
      <div className={cn('truncate font-medium', titleClassName)} title={invitee.email ?? undefined}>
        {invitee.name || invitee.email || `#${invitee.id}`}
      </div>
      {invitee.name && invitee.email && <Sub>{invitee.email}</Sub>}
      {invitee.registeredAt && <Sub>зарегистрирован {formatDate(invitee.registeredAt)}</Sub>}
    </>
  )
}

function StatusBadge({ attribution }: AttributionProps) {
  return <Badge tone={STATUS_TONES[attribution.status]}>{STATUS_LABELS[attribution.status]}</Badge>
}

function StatusNotes({ attribution }: AttributionProps) {
  return (
    <>
      {attribution.activatedAt && <Sub>активен с {formatDate(attribution.activatedAt)}</Sub>}
      {attribution.revertedAt && <Sub>отменён {formatDate(attribution.revertedAt)}</Sub>}
      {attribution.revertReason && (
        <div className="mt-0.5 max-w-[220px] text-[12px] text-ink-soft [overflow-wrap:anywhere] max-md:max-w-none">
          «{attribution.revertReason}»
        </div>
      )}
    </>
  )
}

function KindCell({ attribution }: AttributionProps) {
  return (
    <>
      <div>{ACCOUNT_KIND_LABELS[attribution.kind]}</div>
      {attribution.business && <Sub>{attribution.business.title}</Sub>}
    </>
  )
}

function SourceCell({ attribution }: AttributionProps) {
  return (
    <>
      <div>{sourceLabel(attribution.source)}</div>
      {attribution.createdAt && <Sub>{formatDate(attribution.createdAt)}</Sub>}
    </>
  )
}

function AttributionCard({ attribution, onRevert }: AttributionProps & Pick<AttributionsTableProps, 'onRevert'>) {
  const hasNotes = Boolean(attribution.activatedAt || attribution.revertedAt || attribution.revertReason)

  return (
    <MobileCard className={cn(isFlagged(attribution) && 'bg-warn-tint/40')}>
      <CardHeading aside={<StatusBadge attribution={attribution} />}>
        <div className="[overflow-wrap:anywhere]">
          <InviteeCell invitee={attribution.invitee} titleClassName="text-[15px] font-semibold" />
        </div>
      </CardHeading>

      {attribution.suspicious && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge tone="warn">подозрительно</Badge>
          <span className="text-[12px] text-ink-muted">{suspiciousReasonLabel(attribution.suspiciousReason)}</span>
        </div>
      )}

      <CardFacts>
        <CardFact label="Тип">
          <KindCell attribution={attribution} />
        </CardFact>
        <CardFact label="Как привязан">
          <SourceCell attribution={attribution} />
        </CardFact>
        {hasNotes && (
          <CardFact label="Статус" wide>
            <StatusNotes attribution={attribution} />
          </CardFact>
        )}
      </CardFacts>

      {attribution.status !== 'reverted' && (
        <CardActions>
          <Button variant="secondary" size="sm" onClick={() => onRevert(attribution)}>
            Отменить начисление
          </Button>
        </CardActions>
      )}
    </MobileCard>
  )
}

export function AttributionsTable({ attributions, onRevert }: AttributionsTableProps) {
  return (
    <>
      <MobileCardList label="Приглашённые">
        {attributions.map((attribution) => (
          <AttributionCard key={attribution.id} attribution={attribution} onRevert={onRevert} />
        ))}
      </MobileCardList>

      <DesktopTable>
        <table className="w-full min-w-[860px] text-left text-[13.5px]">
          <thead className="border-b border-line-soft text-[12px] text-ink-muted">
            <tr>
              {HEADERS.map((header) => (
                <th key={header} scope="col" className="px-3.5 py-3 font-medium whitespace-nowrap">
                  {header}
                </th>
              ))}
              <th scope="col" className="px-3.5 py-3">
                <span className="sr-only">Действия</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft align-top">
            {attributions.map((attribution) => (
              <tr key={attribution.id} className={cn(isFlagged(attribution) && 'bg-warn-tint/40')}>
                <td className="max-w-[260px] min-w-[200px] px-3.5 py-3">
                  <InviteeCell invitee={attribution.invitee} />
                </td>
                <td className="max-w-[200px] px-3.5 py-3">
                  <KindCell attribution={attribution} />
                </td>
                <td className="px-3.5 py-3 whitespace-nowrap">
                  <StatusBadge attribution={attribution} />
                  <StatusNotes attribution={attribution} />
                </td>
                <td className="px-3.5 py-3 whitespace-nowrap">
                  <SourceCell attribution={attribution} />
                </td>
                <td className="max-w-[200px] px-3.5 py-3">
                  {attribution.suspicious ? (
                    <>
                      <Badge tone="warn">подозрительно</Badge>
                      <Sub>{suspiciousReasonLabel(attribution.suspiciousReason)}</Sub>
                    </>
                  ) : (
                    <span className="text-ink-faint">—</span>
                  )}
                </td>
                <td className="px-3.5 py-2 text-right whitespace-nowrap">
                  {attribution.status !== 'reverted' && (
                    <Button variant="ghost" size="sm" onClick={() => onRevert(attribution)}>
                      Отменить начисление
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>
    </>
  )
}
